import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';
import * as listCache from '../../services/listCache';

// ── Auth check ───────────────────────────────────────────────────────────────
const isSuperAdmin = () => {
  try {
    if (localStorage.getItem('is_superadmin') === 'true') return true;
    return !!JSON.parse(localStorage.getItem('user') || '{}').is_superadmin;
  } catch { return false; }
};

// ── Constants ────────────────────────────────────────────────────────────────
const SKELETON_WIDTHS = ['90%', '68%', '80%', '55%', '85%', '72%'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// ── Helpers ──────────────────────────────────────────────────────────────────
function normalizeListData(raw) {
  if (!raw) return { items: [], total: null };
  if (Array.isArray(raw)) return { items: raw, total: raw.length };
  if (Array.isArray(raw.results)) return { items: raw.results, total: raw.count ?? raw.results.length };
  if (raw.data !== undefined) {
    const inner = raw.data;
    if (Array.isArray(inner)) return { items: inner, total: inner.length };
    if (inner && Array.isArray(inner.results)) return { items: inner.results, total: inner.count ?? inner.results.length };
  }
  return { items: [], total: null };
}

function buildHeaders() {
  const token = localStorage.getItem('customer_management_token');
  const tenantId = localStorage.getItem('crm_tenant_id');
  const tenantToken = localStorage.getItem('crm_tenant_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    ...(tenantToken ? { 'X-Tenant-Token': tenantToken } : {}),
  };
}

const getCellValue = (item, col) => {
  if (typeof col.value === 'function') {
    if (col.label === 'Access Details') return col.value(item.id ?? item.uuid);
    return col.value(item);
  }
  // Support dot notation for nested fields, e.g. 'assigned_to.username'
  const val = col.value.includes('.')
    ? col.value.split('.').reduce((obj, key) => obj?.[key], item)
    : item[col.value];
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'string' && val.length > 60) return val.slice(0, 60) + '…';
  return val;
};

const getPaths = (pathname, id) => {
  const base = pathname.replace('/list', '');
  let inputBase = base + '/input';
  let detailsBase = base + '/details';
  if (base === '/tasks') {
    inputBase = '/task/input';
    detailsBase = '/task/details';
  } else if (base === '/quote') {
    inputBase = '/quote/input';
    detailsBase = '/single/quote/details';
  } else if (base.startsWith('/customer/interactions')) {
    inputBase = '/customer/interaction/input';
    detailsBase = '/customer/interactions/details';
  }
  return {
    edit: `${inputBase}/${btoa(id)}`,
    details: `${detailsBase}/${btoa(id)}`,
  };
};

const isEditablePath = (path) =>
  path.includes('access-control') ||
  (!path.includes('security') && !path.includes('log') && !path.includes('alert') && !path.includes('overview'));

// ── Page number range ─────────────────────────────────────────────────────────
function pageRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current, current - 1, current + 1].filter(p => p >= 1 && p <= total));
  return [...set].sort((a, b) => a - b);
}

// ── Component ─────────────────────────────────────────────────────────────────
const ListTemplate = ({
  columns,
  data: externalData,      // legacy: caller owns data
  fetchUrl,                // new: ListTemplate fetches internally
  onEdit,
  onDelete,
  onAccessDetails,
  createPath,
  pageTitle,
  loading: externalLoading,
  searchable,
  // Kept for backward-compat; ignored in server mode
  onNextPage,
  onPrevPage,
}) => {
  const { pathname } = useLocation();
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [remoteMatches, setRemoteMatches] = useState(null); // backend fallback results, or null
  const [remoteLoading, setRemoteLoading] = useState(false);
  const searchDebounceRef = useRef(null);

  // ── Pagination state ────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── Server-fetch state ──────────────────────────────────────────────────────
  const [serverItems, setServerItems] = useState([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverLoading, setServerLoading] = useState(false);
  const [fetchVersion, setFetchVersion] = useState(0); // increment → refetch

  const isServerMode = Boolean(fetchUrl);

  useEffect(() => {
    if (!fetchUrl) return;
    let cancelled = false;

    const sep = fetchUrl.includes('?') ? '&' : '?';
    const url = `${fetchUrl}${sep}page=${page}&page_size=${pageSize}`;

    const applyRaw = (raw) => {
      const { items, total } = normalizeListData(raw);
      setServerItems(items);
      setServerTotal(total ?? items.length);
    };

    // Stale-while-revalidate: render any cached data immediately (no spinner),
    // only hit the network if there's nothing cached or it's past its freshness
    // window — this is what makes revisiting a list page instant instead of
    // re-fetching every single time.
    const cached = listCache.get(url);
    if (cached) {
      applyRaw(cached.data);
      setServerLoading(false);
    } else {
      setServerLoading(true);
    }

    if (!cached || cached.isStale) {
      listCache
        .fetchDeduped(url, buildHeaders())
        .then(raw => {
          if (cancelled) return;
          listCache.set(url, raw);
          applyRaw(raw);
          setServerLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setServerLoading(false);
        });
    }

    return () => { cancelled = true; };
  }, [fetchUrl, page, pageSize, fetchVersion]);

  const refetch = useCallback(() => setFetchVersion(v => v + 1), []);

  // ── Derive active data ──────────────────────────────────────────────────────
  const loading = isServerMode ? serverLoading : externalLoading;

  // Legacy mode: normalize + sort external data, then slice
  const legacyNormalized = React.useMemo(() => {
    if (isServerMode) return [];
    const { items } = normalizeListData(externalData);
    return [...items].sort((a, b) => {
      const da = a.created_at || a.date || a.timestamp || a.interaction_date || a.activity_date || a.due_date;
      const db = b.created_at || b.date || b.timestamp || b.interaction_date || b.activity_date || b.due_date;
      if (da && db) {
        const ta = new Date(da).getTime(), tb = new Date(db).getTime();
        if (!isNaN(ta) && !isNaN(tb)) return tb - ta;
      }
      const idA = a.id ?? a.uuid;
      const idB = b.id ?? b.uuid;
      if (typeof idA === 'number' && typeof idB === 'number') return idB - idA;
      return String(idB || '').localeCompare(String(idA || ''));
    });
  }, [externalData, isServerMode]);

  useEffect(() => { if (!isServerMode) setPage(1); }, [legacyNormalized.length, isServerMode]);

  const totalItems = isServerMode ? serverTotal : legacyNormalized.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePageSize = pageSize;

  // What's actually rendered in the table
  const displayRows = isServerMode
    ? serverItems
    : legacyNormalized.slice((page - 1) * safePageSize, page * safePageSize);

  // Info string
  const showFrom = totalItems === 0 ? 0 : (page - 1) * safePageSize + 1;
  const showTo = Math.min(page * safePageSize, totalItems);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const goTo = (p) => setPage(Math.max(1, Math.min(p, totalPages)));
  const changePageSize = (n) => { setPageSize(n); setPage(1); };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const invalidateThisList = useCallback(() => {
    if (fetchUrl) listCache.invalidate((u) => u.startsWith(fetchUrl));
  }, [fetchUrl]);

  const handleDeleteConfirm = () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    if (!onDelete) return;
    const result = onDelete(id);
    if (isServerMode) {
      invalidateThisList();
      if (result && typeof result.then === 'function') result.then(refetch).catch(refetch);
      else setTimeout(refetch, 400);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  const cleanColumns = columns.filter(
    c => c.label !== 'Access Details' && c.label !== 'ACCESS DETAILS'
  );
  const editable = isEditablePath(pathname);
  const pages = pageRange(page, totalPages);

  // Search: check the current page in memory first; only hit the backend
  // (same ?search= endpoint AsyncSelect's Trie-backed search uses) when
  // nothing on this page matches — never a fresh full fetch.
  const q = search.trim().toLowerCase();
  const filteredRows = q
    ? displayRows.filter(item =>
        cleanColumns.some(col => String(getCellValue(item, col)).toLowerCase().includes(q))
      )
    : displayRows;

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!isServerMode || q.length < 2 || filteredRows.length > 0) {
      setRemoteMatches(null);
      setRemoteLoading(false);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      const url = `${fetchUrl}${fetchUrl.includes('?') ? '&' : '?'}search=${encodeURIComponent(q)}&page_size=${pageSize}`;
      const cached = listCache.get(url);
      const apply = (raw) => setRemoteMatches(normalizeListData(raw).items);
      if (cached && !cached.isStale) { apply(cached.data); return; }
      setRemoteLoading(true);
      listCache.fetchDeduped(url, buildHeaders())
        .then(raw => { listCache.set(url, raw); apply(raw); })
        .catch(() => setRemoteMatches([]))
        .finally(() => setRemoteLoading(false));
    }, 350);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, isServerMode, fetchUrl, pageSize, filteredRows.length]);

  const visibleRows = (q && filteredRows.length === 0 && remoteMatches) ? remoteMatches : filteredRows;
  const searchPlaceholder = `Search by ${cleanColumns.slice(0, 3).map(c => c.label.toLowerCase()).join(', ')}…`;

  return (
    <>
      <div className="table-wrap">
        {searchable !== false && (
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>🔍</span>
              <input
                className="form-input"
                style={{ paddingLeft: 32 }}
                placeholder={searchPlaceholder}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
              {loading
                ? 'Loading…'
                : q
                  ? remoteLoading
                    ? 'Searching…'
                    : visibleRows === remoteMatches
                      ? `${visibleRows.length.toLocaleString()} match${visibleRows.length !== 1 ? 'es' : ''}`
                      : `${filteredRows.length.toLocaleString()} match${filteredRows.length !== 1 ? 'es' : ''} on this page`
                  : `${totalItems.toLocaleString()} record${totalItems !== 1 ? 's' : ''}`}
            </span>
          </div>
        )}
        <div className="table-scroll">
          <table className="crm-table">
            <thead>
              <tr>
                {cleanColumns.map((col, i) => <th key={i}>{col.label}</th>)}
                <th style={{ textAlign: 'right', width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: Math.min(pageSize, 6) }).map((_, ri) => (
                    <tr key={ri}>
                      {cleanColumns.map((_, ci) => (
                        <td key={ci}>
                          <div className="skeleton" style={{ height: 13, width: SKELETON_WIDTHS[(ri + ci) % SKELETON_WIDTHS.length], borderRadius: 4 }} />
                        </td>
                      ))}
                      <td><div className="skeleton" style={{ height: 26, width: 140, borderRadius: 4 }} /></td>
                    </tr>
                  ))
                : visibleRows.length === 0
                  ? null
                  : visibleRows.map(item => {
                      const paths = getPaths(pathname, item.id);
                      return (
                        <tr key={item.id}>
                          {cleanColumns.map((col, i) => <td key={i}>{getCellValue(item, col)}</td>)}
                          <td className="actions-cell">
                            <div className="table-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                              <button className="btn btn-secondary btn-sm" onClick={() => { window.location.href = paths.details; }}>
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/></svg>
                                View
                              </button>
                              {editable && (
                                <button className="btn btn-secondary btn-sm" onClick={() => { window.location.href = paths.edit; }}>
                                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"/></svg>
                                  Edit
                                </button>
                              )}
                              {onDelete && isSuperAdmin() && (
                                <button className="btn btn-danger btn-sm" onClick={() => setPendingDeleteId(item.id)}>
                                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M2 4h12M6 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4"/></svg>
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
              }
            </tbody>
          </table>
        </div>

        {!loading && !remoteLoading && visibleRows.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon-wrap">
              {q ? (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
                </svg>
              ) : (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 8v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
                  <path d="M3 8l2.5-4.5A1 1 0 0 1 6.37 3h11.26a1 1 0 0 1 .87.5L21 8" />
                  <path d="M3 8h18" /><path d="M9 12h6" />
                </svg>
              )}
            </div>
            <p className="empty-state-title">{q ? 'No matching records' : 'No records found'}</p>
            <p className="empty-state-text">
              {q ? `We couldn't find anything for "${search}".` : 'There’s nothing to display here yet.'}
            </p>
            {q && (
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 16 }} onClick={() => setSearch('')}>
                Clear search
              </button>
            )}
          </div>
        )}

        {/* Pagination bar */}
        {(isServerMode || totalItems > 0) && !loading && (
          <div className="pagination-wrap">
            {/* Info */}
            <span className="pagination-info">
              {totalItems === 0
                ? 'No records'
                : `${showFrom.toLocaleString()}–${showTo.toLocaleString()} of ${totalItems.toLocaleString()}`}
            </span>

            {/* Page buttons */}
            <div className="pagination-buttons">
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(1)} disabled={page === 1} title="First">«</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page - 1)} disabled={page === 1} title="Previous">‹</button>

              {pages.map((p, idx) => {
                const prev = pages[idx - 1];
                return (
                  <React.Fragment key={p}>
                    {prev && p - prev > 1 && (
                      <span className="pagination-ellipsis">…</span>
                    )}
                    <button
                      className={`btn btn-sm pagination-page-btn${p === page ? ' active' : ''}`}
                      onClick={() => goTo(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}

              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page + 1)} disabled={page === totalPages} title="Next">›</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(totalPages)} disabled={page === totalPages} title="Last">»</button>
            </div>

            {/* Page-size selector */}
            <div className="pagination-size-wrap">
              <label className="pagination-size-label">Rows:</label>
              <select
                className="pagination-size-select"
                value={pageSize}
                onChange={e => changePageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {onDelete && isSuperAdmin() && (
          <ConfirmModal
            open={pendingDeleteId !== null}
            title="Delete Record"
            message="Are you sure you want to delete this record? This action cannot be undone."
            confirmLabel="Delete"
            confirmDanger
            onConfirm={handleDeleteConfirm}
            onCancel={() => setPendingDeleteId(null)}
          />
        )}
      </div>
    </>
  );
};

export default ListTemplate;
